import { useSessionStore } from './token-store';

/**
 * THE OTHER HALF OF SPEC §2.5's IMPERSONATION.
 *
 * ══ WHAT WAS MISSING ═══════════════════════════════════════════════════════
 *
 * The console mints a real, clinic-scoped, short-lived STAFF token and builds
 * `https://app.bookora.net/#impersonation=token=…&clinicId=…&sessionId=…`. It
 * has always built that link. Nothing in this panel read `location.hash`, so
 * the «باز کردن پنل کلینیک» button landed the operator on the login screen
 * while a live impersonation session sat open on the server for up to an hour.
 * The most consequential capability in the product had a complete server, a
 * complete console and no consumer.
 *
 * ══ WHY THE FRAGMENT ═══════════════════════════════════════════════════════
 *
 * A URL fragment is never sent to a server: it stays out of nginx's access log,
 * out of `Referer`, and out of any upstream proxy's history. That is the whole
 * reason the console puts the token there instead of in a query string — and
 * it is why this function's FIRST job, before it validates anything, is to wipe
 * the fragment out of the address bar and out of the history entry, so a
 * screenshot or a shoulder-surfer does not carry a working clinic token.
 *
 * ══ WHAT IT DELIBERATELY DOES NOT TRUST ════════════════════════════════════
 *
 * Nothing. The fragment is attacker-controllable — anyone can send a staff
 * member a link with a token in it. What makes that harmless is that the token
 * is a JWT this panel never inspects and never grants anything on the strength
 * of: every screen's data comes from the API, which verifies the signature, the
 * session and the audience on every request. A forged fragment produces a
 * session that 401s immediately. This function's job is transport, not
 * authorisation.
 *
 * The one thing it does enforce is the SHAPE, so a malformed link fails as an
 * ignored fragment rather than as a half-written session.
 *
 * ══ IT REPLACES ANY SESSION ALREADY IN THIS BROWSER ════════════════════════
 *
 * Deliberately, and it is why `clearSession()` runs first. Merging an
 * impersonation into an existing session would leave the operator's own
 * permissions cached beside the impersonated clinic's token — two identities in
 * one store, which is the state the audience boundary exists to prevent. A
 * support operator opening this link is asking to become that staff member in
 * this tab; when the token expires the panel signs out, because an
 * impersonation session mints no refresh token by design.
 */

const FRAGMENT_KEY = 'impersonation';

export interface ImpersonationHandoff {
  accessToken: string;
  clinicId: string;
  sessionId: string;
  /** ISO instant, when the console sent one. Absent on older links. */
  expiresAt: string | null;
}

/**
 * Parses `#impersonation=<urlencoded querystring>` out of a raw hash.
 *
 * Exported for the test: the parsing rules are the part worth pinning, and a
 * test that has to drive `window.location` to reach them tests jsdom instead.
 */
export function parseImpersonationFragment(hash: string): ImpersonationHandoff | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.startsWith(`${FRAGMENT_KEY}=`)) return null;

  let inner: string;
  try {
    inner = decodeURIComponent(raw.slice(FRAGMENT_KEY.length + 1));
  } catch {
    // A malformed percent-escape is a malformed link, not a session.
    return null;
  }

  const params = new URLSearchParams(inner);
  const accessToken = params.get('token');
  const clinicId = params.get('clinicId');
  const sessionId = params.get('sessionId');
  if (!accessToken || !clinicId || !sessionId) return null;

  return {
    accessToken,
    clinicId,
    sessionId,
    expiresAt: params.get('expiresAt'),
  };
}

/**
 * Reads the handoff out of the address bar, installs it, and erases the trace.
 *
 * Returns true when a session was established, so the caller can skip a
 * redirect to `/login` that would otherwise race the first `/auth/me`.
 *
 * Call it during render, not in an effect: an effect runs after
 * `AuthProvider`'s boot has already decided there is no token.
 */
export function consumeImpersonationHandoff(): boolean {
  if (typeof window === 'undefined') return false;

  const handoff = parseImpersonationFragment(window.location.hash);
  if (!handoff) return false;

  // FIRST. Before the store, before anything that could throw — a token left in
  // the address bar is the failure mode this whole fragment scheme exists to
  // avoid. `replaceState` also drops it from the history entry, so Back does
  // not bring it back.
  clearFragment();

  // Identity is NOT taken from the fragment. `/auth/me` is the authority and
  // `AuthProvider` calls it on the very next tick; until it answers, the panel
  // knows it holds an impersonation token and nothing else. Putting a name from
  // client-supplied data on screen is exactly the thing this must not do.
  useSessionStore.getState().beginImpersonation({
    accessToken: handoff.accessToken,
    expiresIn: expiresInSeconds(handoff.expiresAt),
    sessionId: handoff.sessionId,
    activeClinicId: handoff.clinicId,
  });

  return true;
}

function clearFragment(): void {
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
}

/**
 * Seconds of life left, from the console's `expiresAt`.
 *
 * The fallback is deliberately SHORT rather than long: this stamp only drives
 * the panel's own "should I bother sending this request" check, and being
 * pessimistic costs a redundant 401 while being optimistic costs a stale
 * session that looks alive. The server's `exp` is the real limit either way.
 */
function expiresInSeconds(expiresAt: string | null): number {
  if (!expiresAt) return 60;
  const left = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
  return Number.isFinite(left) && left > 0 ? left : 60;
}
